import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Testimonials.css';

const testimonials = [
  {
    name: "Sarah Mitchell",
    role: "Software Engineer at Google",
    image: "👩‍💻",
    text: "The Interview Assistant helped me land my dream job at Google. The AI feedback was incredibly accurate and helped me refine my answers to perfection."
  },
  {
    name: "Michael Chen",
    role: "Product Manager at Amazon",
    image: "👨‍💼",
    text: "Resume Builder saved me hours of work. The ATS optimization feature ensured my resume got past automated filters and into recruiters' hands."
  },
  {
    name: "Emily Rodriguez",
    role: "Data Scientist at Microsoft",
    image: "👩‍🔬",
    text: "The Code Breakdown tool is a game-changer! It helped me understand complex algorithms during my interview prep. Highly recommend!"
  },
  {
    name: "David Thompson",
    role: "UX Designer at Apple",
    image: "👨‍🎨",
    text: "Career Path Advisor gave me clarity on my next steps. The personalized recommendations were spot-on and helped me transition smoothly."
  },
  {
    name: "Jessica Park",
    role: "Marketing Lead at Netflix",
    image: "👩‍💼",
    text: "I used multiple tools together - Resume Builder, Tailoring, and ATS Check. Got 3 interview calls in one week! This platform is phenomenal."
  },
  {
    name: "James Wilson",
    role: "DevOps Engineer at Meta",
    image: "👨‍💻",
    text: "The AI Quiz helped me identify my weak areas and focus my prep. Went from nervous to confident in just 2 weeks!"
  },
  {
    name: "Amanda Lee",
    role: "Business Analyst at LinkedIn",
    image: "👩‍💼",
    text: "The AI Portfolio Builder made it so easy to create a professional portfolio. Recruiters actually commented on how polished it looked!"
  },
  {
    name: "Robert Garcia",
    role: "Full Stack Developer at Uber",
    image: "👨‍💻",
    text: "Interview Assistant's realistic scenarios prepared me for tough behavioral questions. I felt like I'd already done the interview before!"
  }
];

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(3);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const autoplayRef = useRef(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const total = testimonials.length;

  const goToIndex = useCallback((index) => {
    if (isAnimating || index === currentIndex) return;
    setIsAnimating(true);
    setCurrentIndex(index);
    setTimeout(() => setIsAnimating(false), 600);
  }, [isAnimating, currentIndex]);

  const navigate = useCallback((dir) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex(prev => {
      let next = prev + dir;
      if (next < 0) next = total - 1;
      if (next >= total) next = 0;
      return next;
    });
    setTimeout(() => setIsAnimating(false), 600);
  }, [isAnimating, total]);

  const stopAutoplay = useCallback(() => {
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
      autoplayRef.current = null;
    }
    setIsPlaying(false);
  }, []);

  const startAutoplay = useCallback(() => {
    if (autoplayRef.current) clearInterval(autoplayRef.current);
    autoplayRef.current = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % total);
    }, 4000);
    setIsPlaying(true);
  }, [total]);

  const toggleAutoplay = () => {
    if (isPlaying) stopAutoplay();
    else startAutoplay();
  };

  useEffect(() => {
    startAutoplay();
    return () => { if (autoplayRef.current) clearInterval(autoplayRef.current); };
  }, [startAutoplay]);

  const handleUserNav = (fn) => {
    stopAutoplay();
    fn();
  };

  const getItemStyle = (index) => {
    let offset = index - currentIndex;
    if (offset > total / 2) offset -= total;
    else if (offset < -total / 2) offset += total;

    const absOffset = Math.abs(offset);
    const sign = Math.sign(offset);

    let translateX = offset * 260;
    let translateZ = -absOffset * 180;
    let rotateY = -sign * Math.min(absOffset * 55, 55);
    let opacity = 1 - absOffset * 0.2;
    let scale = 1 - absOffset * 0.08;

    if (absOffset > 3) {
      opacity = 0;
      translateX = sign * 900;
    }

    return {
      transform: `translateX(${translateX}px) translateZ(${translateZ}px) rotateY(${rotateY}deg) scale(${scale})`,
      opacity,
      zIndex: 100 - absOffset,
      transition: 'all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    };
  };

  // Touch handlers
  const onTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].screenX;
    touchStartY.current = e.changedTouches[0].screenY;
  };
  const onTouchEnd = (e) => {
    const diffX = touchStartX.current - e.changedTouches[0].screenX;
    const diffY = touchStartY.current - e.changedTouches[0].screenY;
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 30) {
      handleUserNav(() => navigate(diffX > 0 ? 1 : -1));
    }
  };

  const current = testimonials[currentIndex];

  return (
    <section className="testimonials-section" id="testimonials">
      <h1>Success Stories</h1>

      {/* Active card info */}
      <br></br><br></br>

      {/* 3D Coverflow */}
      <div
        className="tm-coverflow-container"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') handleUserNav(() => navigate(-1));
          if (e.key === 'ArrowRight') handleUserNav(() => navigate(1));
        }}
        tabIndex={0}
      >
        <div className="tm-coverflow">
          {testimonials.map((t, i) => (
            <div
              key={i}
              className={`tm-item ${i === currentIndex ? 'active' : ''}`}
              style={getItemStyle(i)}
              onClick={() => handleUserNav(() => goToIndex(i))}
            >
              {/* Card front */}
              <div className="tm-card glass">
                <div className="tm-card-header">
                  <div className="tm-avatar">{t.image}</div>
                  <div className="tm-card-info">
                    <h4 className="tm-card-name">{t.name}</h4>
                    <p className="tm-card-role">{t.role}</p>
                  </div>
                </div>
                <p className="tm-text">"{t.text}"</p>
              </div>

              {/* Reflection */}
              <div className="tm-reflection" />
            </div>
          ))}
        </div>

        {/* Nav buttons */}
        <button className="tm-nav prev" onClick={() => handleUserNav(() => navigate(-1))}>‹</button>
        <button className="tm-nav next" onClick={() => handleUserNav(() => navigate(1))}>›</button>

        {/* Dots */}
        <div className="tm-dots">
          {testimonials.map((_, i) => (
            <div
              key={i}
              className={`tm-dot ${i === currentIndex ? 'active' : ''}`}
              onClick={() => handleUserNav(() => goToIndex(i))}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;